import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  Alert,
  AlertTitle,
  InputAdornment,
  FormControlLabel,
  Switch,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Paper,
  Collapse
} from '@mui/material';

import {
  Star as StarIcon,
  EmojiEvents as EmojiEventsIcon,
  MilitaryTech as MilitaryTechIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Groups as GroupsIcon,
  Campaign as CampaignIcon,
  Payment as PaymentIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  VisibilityOff as VisibilityOffIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  AccountCircle as AccountCircleIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Lightbulb as LightbulbIcon,
  Calculate as CalculateIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

import { useQuery, useMutation } from 'react-query';
import client from '../../../../API'; // adjust if needed

// Icon options with Material-UI icons
const iconOptions = [
  { label: 'Star', value: 'Star', icon: <StarIcon /> },
  { label: 'Trophy', value: 'EmojiEvents', icon: <EmojiEventsIcon /> },
  { label: 'Medal', value: 'MilitaryTech', icon: <MilitaryTechIcon /> },
];

// Icon map for display
const iconMap = {
  Star: <StarIcon />,
  EmojiEvents: <EmojiEventsIcon />,
  MilitaryTech: <MilitaryTechIcon />
};

// User type configuration
const userTypeConfig = {
  global: { 
    icon: <PublicIcon fontSize="small" />, 
    label: 'All Users', 
    color: '#2196F3',
    description: 'Available to all users (both regular and creator users)'
  },
  creator: { 
    icon: <BusinessIcon fontSize="small" />, 
    label: 'Creators Only', 
    color: '#FF9800',
    description: 'Only available to creator users'
  },
  user: { 
    icon: <AccountCircleIcon fontSize="small" />, 
    label: 'Regular Users Only', 
    color: '#4CAF50',
    description: 'Only available to regular (non-creator) users'
  }
};

// Criteria configuration with icons and brand colors (hex)
const criteriaConfig = {
  partnership: { icon: <GroupsIcon fontSize="small" />, label: 'Partnerships', color: '#4CAF50' },
  campaign: { icon: <CampaignIcon fontSize="small" />, label: 'Campaigns', color: '#2196F3' },
  payout: { icon: <PaymentIcon fontSize="small" />, label: 'Payouts', color: '#FF9800' },
  referral: { icon: <PersonAddIcon fontSize="small" />, label: 'Referrals', color: '#9C27B0' },
  successful_referral: { icon: <CheckCircleIcon fontSize="small" />, label: 'Successful Referrals', color: '#4CAF50' }
};

// Scoring weights from documentation
const scoringWeights = {
  payout: { user: 1.2, creator: 0.6 },
  partnership: { user: 3.0, creator: 3.0 },
  campaign: { user: 2.0, creator: 3.0 },
  referral: 5.0 // per completed pair
};

// Preset recommendations
const presetRecommendations = [
  {
    title: "First Partnership",
    description: "Reward users for making their first partnership connection",
    userType: "global",
    criteria: { partnership: 1 },
    category: "Beginner"
  },
  {
    title: "Deal Maker",
    description: "Recognize active users completing multiple payouts",
    userType: "user",
    criteria: { payout: 5 },
    category: "Intermediate"
  },
  {
    title: "Creator Superstar",
    description: "Celebrate creators with consistent campaign success",
    userType: "creator",
    criteria: { campaign: 10, payout: 20 },
    category: "Advanced"
  },
  {
    title: "Community Builder",
    description: "Reward successful referral activity",
    userType: "global",
    criteria: { referral: 3, successful_referral: 3 },
    category: "Growth"
  },
  {
    title: "Multi-Activity Champion",
    description: "Recognition for well-rounded platform engagement",
    userType: "global",
    criteria: { partnership: 2, campaign: 1, payout: 3 },
    category: "Expert"
  }
];

const BlitzBoltDetailsView = () => {
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);

  // Basic info
  const [newBoltTitle, setNewBoltTitle] = useState('');
  const [newBoltDescription, setNewBoltDescription] = useState('');
  const [newBoltIconName, setNewBoltIconName] = useState('Star');
  const [newBoltPoints, setNewBoltPoints] = useState(0);

  // Privacy and user type settings
  const [isPrivate, setIsPrivate] = useState(false);
  const [userType, setUserType] = useState('global');

  // Requirements
  const [partnershipRequirement, setPartnershipRequirement] = useState(0);
  const [campaignRequirement, setCampaignRequirement] = useState(0);
  const [payoutRequirement, setPayoutRequirement] = useState(0);
  const [referralRequirement, setReferralRequirement] = useState(0);
  const [successfulReferralRequirement, setSuccessfulReferralRequirement] = useState(0);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    userType: 'all',
    privacy: 'all',
    achievementStatus: 'all',
    pointsRange: [0, 100],
    criteriaTypes: [],
    minPoints: '',
    maxPoints: ''
  });

  // Optional user-type info
  const [userTypeInfo, setUserTypeInfo] = useState(null);

  // Fetch data
  const {
    data: blitzBoltsResponse,
    isLoading: isBlitzBoltsLoading,
    refetch: refetchBlitzBolts
  } = useQuery('listBlitzBolts', () => client.blitzbolts.list(), {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data && data.user_info) setUserTypeInfo(data.user_info);
    }
  });

  // Normalize data shape
  const blitzBolts = blitzBoltsResponse?.bolts || blitzBoltsResponse || [];

  // Calculate recommended points based on current requirements
  const calculateRecommendedPoints = (requirements, targetUserType) => {
    let totalPoints = 0;
    
    Object.entries(requirements).forEach(([criteriaType, count]) => {
      if (count > 0) {
        if (criteriaType === 'referral') {
          // For referrals, use min of referral and successful_referral as completed pairs
          const pairs = Math.min(requirements.referral || 0, requirements.successful_referral || 0);
          totalPoints += pairs * scoringWeights.referral;
        } else if (scoringWeights[criteriaType]) {
          const weight = scoringWeights[criteriaType][targetUserType] || scoringWeights[criteriaType].user || scoringWeights[criteriaType];
          totalPoints += count * weight;
        }
      }
    });
    
    return Math.round(totalPoints);
  };

  // Get current recommended points
  const currentRecommendedPoints = useMemo(() => {
    const requirements = {
      partnership: Number(partnershipRequirement) || 0,
      campaign: Number(campaignRequirement) || 0,
      payout: Number(payoutRequirement) || 0,
      referral: Number(referralRequirement) || 0,
      successful_referral: Number(successfulReferralRequirement) || 0
    };
    
    return calculateRecommendedPoints(requirements, userType);
  }, [partnershipRequirement, campaignRequirement, payoutRequirement, referralRequirement, successfulReferralRequirement, userType]);

  // Filter bolts based on current filters
  const filteredBolts = useMemo(() => {
    if (!blitzBolts) return [];
    
    return blitzBolts.filter(bolt => {
      // User type filter
      if (filters.userType !== 'all' && bolt.user_type !== filters.userType) return false;
      
      // Privacy filter
      if (filters.privacy === 'public' && bolt.is_private) return false;
      if (filters.privacy === 'private' && !bolt.is_private) return false;
      
      // Achievement status filter
      if (filters.achievementStatus === 'achieved' && !bolt.attainment?.achieved) return false;
      if (filters.achievementStatus === 'not-achieved' && bolt.attainment?.achieved) return false;
      
      // Points range filter
      const minPoints = filters.minPoints ? Number(filters.minPoints) : 0;
      const maxPoints = filters.maxPoints ? Number(filters.maxPoints) : Infinity;
      if (bolt.points < minPoints || bolt.points > maxPoints) return false;
      
      // Criteria types filter
      if (filters.criteriaTypes.length > 0) {
        const boltCriteriaTypes = Object.keys(bolt.criteria || {});
        const hasMatchingCriteria = filters.criteriaTypes.some(filterType => 
          boltCriteriaTypes.includes(filterType)
        );
        if (!hasMatchingCriteria) return false;
      }
      
      return true;
    });
  }, [blitzBolts, filters]);

  // Create mutation
  const createBlitzBoltMutation = useMutation(
    (input) => client.blitzbolts.create(input),
    {
      onSuccess: () => {
        refetchBlitzBolts();
        resetForm();
      },
      onError: (error) => {
        console.error('Error creating BlitzBolt:', error);
        alert('Failed to create BlitzBolt');
      }
    }
  );

  const resetForm = () => {
    setNewBoltTitle('');
    setNewBoltDescription('');
    setNewBoltIconName('Star');
    setNewBoltPoints(0);
    setIsPrivate(false);
    setUserType('global');
    setPartnershipRequirement(0);
    setCampaignRequirement(0);
    setPayoutRequirement(0);
    setReferralRequirement(0);
    setSuccessfulReferralRequirement(0);
    setDialogTab(0);
  };

  const clearFilters = () => {
    setFilters({
      userType: 'all',
      privacy: 'all',
      achievementStatus: 'all',
      pointsRange: [0, 100],
      criteriaTypes: [],
      minPoints: '',
      maxPoints: ''
    });
  };

  const applyPresetRecommendation = (preset) => {
    setNewBoltTitle(preset.title);
    setNewBoltDescription(preset.description);
    setUserType(preset.userType);
    
    // Apply criteria
    setPartnershipRequirement(preset.criteria.partnership || 0);
    setCampaignRequirement(preset.criteria.campaign || 0);
    setPayoutRequirement(preset.criteria.payout || 0);
    setReferralRequirement(preset.criteria.referral || 0);
    setSuccessfulReferralRequirement(preset.criteria.successful_referral || 0);
    
    // Switch to main tab
    setDialogTab(0);
  };

  const handleCreateBlitzBolt = () => {
    if (!newBoltTitle.trim()) {
      alert('Please enter a title for the BlitzBolt');
      return;
    }

    const criteria = {};
    if (partnershipRequirement > 0) criteria.partnership = Number(partnershipRequirement);
    if (campaignRequirement > 0) criteria.campaign = Number(campaignRequirement);
    if (payoutRequirement > 0) criteria.payout = Number(payoutRequirement);
    if (referralRequirement > 0) criteria.referral = Number(referralRequirement);
    if (successfulReferralRequirement > 0) criteria.successful_referral = Number(successfulReferralRequirement);

    const finalPoints = newBoltPoints > 0 ? parseInt(newBoltPoints, 10) : currentRecommendedPoints;

    const newBlitzBoltData = {
      title: newBoltTitle,
      description: newBoltDescription,
      icon_name: newBoltIconName,
      points: finalPoints,
      criteria,
      is_private: isPrivate,
      user_type: userType
    };

    createBlitzBoltMutation.mutate(newBlitzBoltData);
    setOpenDialog(false);
  };

  // Colored chips
  const coloredChipSX = (hex) => ({
    backgroundColor: `${hex}20`,
    color: hex,
    border: `1px solid ${hex}40`,
    '& .MuiChip-icon': { color: hex }
  });

  // Render filter section
  const renderFilterSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            Filters ({filteredBolts.length} of {blitzBolts.length} bolts)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={JSON.stringify(filters) === JSON.stringify({
                userType: 'all',
                privacy: 'all',
                achievementStatus: 'all',
                pointsRange: [0, 100],
                criteriaTypes: [],
                minPoints: '',
                maxPoints: ''
              })}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={showFilters ? <ExpandMoreIcon /> : <FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Stack>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>User Type</InputLabel>
                <Select
                  value={filters.userType}
                  label="User Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, userType: e.target.value }))}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="global">All Users</MenuItem>
                  <MenuItem value="creator">Creators Only</MenuItem>
                  <MenuItem value="user">Regular Users Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Privacy</InputLabel>
                <Select
                  value={filters.privacy}
                  label="Privacy"
                  onChange={(e) => setFilters(prev => ({ ...prev, privacy: e.target.value }))}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="public">Public Only</MenuItem>
                  <MenuItem value="private">Private Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.achievementStatus}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, achievementStatus: e.target.value }))}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="achieved">Achieved</MenuItem>
                  <MenuItem value="not-achieved">Not Achieved</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Criteria</InputLabel>
                <Select
                  multiple
                  value={filters.criteriaTypes}
                  label="Criteria"
                  onChange={(e) => setFilters(prev => ({ ...prev, criteriaTypes: e.target.value }))}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={criteriaConfig[value]?.label} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {Object.entries(criteriaConfig).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box display="flex" alignItems="center">
                        {config.icon}
                        <Box ml={1}>{config.label}</Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Min Points"
                size="small"
                type="number"
                value={filters.minPoints}
                onChange={(e) => setFilters(prev => ({ ...prev, minPoints: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Max Points"
                size="small"
                type="number"
                value={filters.maxPoints}
                onChange={(e) => setFilters(prev => ({ ...prev, maxPoints: e.target.value }))}
                fullWidth
              />
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );

  // Requirements chips
  const renderCriteriaChips = (criteria) => {
    if (!criteria || Object.keys(criteria).length === 0) {
      return <Chip label="No requirements" size="small" variant="outlined" />;
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {Object.entries(criteria).map(([key, value]) => {
          const config = criteriaConfig[key];
          if (!config) return null;
          return (
            <Chip
              key={key}
              icon={config.icon}
              label={`${value} ${config.label}`}
              size="small"
              variant="outlined"
              sx={coloredChipSX(config.color)}
            />
          );
        })}
      </Stack>
    );
  };

  // Progress chips
  const renderProgressChips = (progress = {}, criteria = {}) => {
    const entries = Object.entries(progress);
    if (entries.length === 0) return null;

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {entries.map(([key, current]) => {
          const required = criteria?.[key] ?? 0;
          const config = criteriaConfig[key];
          if (!config) return null;

          const completed = Number(current) >= Number(required);
          return (
            <Chip
              key={key}
              icon={config.icon}
              label={`${current}/${required} ${config.label}`}
              size="small"
              variant={completed ? 'filled' : 'outlined'}
              sx={completed ? coloredChipSX(config.color) : undefined}
            />
          );
        })}
      </Stack>
    );
  };

  // Render user type and privacy chips
  const renderMetadataChips = (bolt) => {
    const userTypeInfo = userTypeConfig[bolt.user_type] || userTypeConfig.global;
    
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip
          icon={userTypeInfo.icon}
          label={userTypeInfo.label}
          size="small"
          variant="outlined"
          sx={coloredChipSX(userTypeInfo.color)}
        />
        {bolt.is_private && (
          <Chip
            icon={<VisibilityOffIcon fontSize="small" />}
            label="Private"
            size="small"
            variant="outlined"
            sx={coloredChipSX('#757575')}
          />
        )}
      </Stack>
    );
  };

  // Enhanced header
  const renderEnhancedHeader = () => (
    <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              BlitzBolt Management
              {userTypeInfo && (
                <Chip
                  icon={userTypeInfo.user_type === 'creator' ? <BusinessIcon /> : <PersonIcon />}
                  label={`${(userTypeInfo.user_type || 'user').toUpperCase()} USER`}
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    color: 'white'
                  }}
                />
              )}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Create and manage achievement challenges for your users
            </Typography>

            {userTypeInfo && userTypeInfo.user_type === 'creator' && (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '& .MuiAlert-icon': { color: 'white' }
                }}
              >
                <AlertTitle>Creator Mode</AlertTitle>
                Payouts are tracked as received (not sent). Partnerships and campaigns may be limited until creator-specific models are implemented.
              </Alert>
            )}
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            Create BlitzBolt
          </Button>
        </Box>

        {/* Activity Summary */}
        {userTypeInfo?.activity_counts && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Current Activity Summary for {userTypeInfo.username}:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                icon={criteriaConfig.partnership.icon}
                label={`${userTypeInfo.activity_counts.partnerships || 0} Partnerships`}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={criteriaConfig.campaign.icon}
                label={`${userTypeInfo.activity_counts.campaigns || 0} Campaigns`}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={criteriaConfig.payout.icon}
                label={`${userTypeInfo.activity_counts.payouts || 0} Payouts ${userTypeInfo.user_type === 'creator' ? 'Received' : 'Sent'}`}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={criteriaConfig.referral.icon}
                label={`${userTypeInfo.activity_counts.referrals || 0} Referrals`}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={criteriaConfig.successful_referral.icon}
                label={`${userTypeInfo.activity_counts.successful_referrals || 0} Successful`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Recommendations tab content
  const renderRecommendationsTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LightbulbIcon />
        Achievement Recommendations
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Smart Scoring</AlertTitle>
        These recommendations use the platform's scoring formula to suggest optimal point values based on activity weights.
      </Alert>

      <Grid container spacing={2}>
        {presetRecommendations.map((preset, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  transform: 'translateY(-2px)', 
                  boxShadow: 3 
                }
              }}
              onClick={() => applyPresetRecommendation(preset)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Typography variant="h6" component="h3">
                    {preset.title}
                  </Typography>
                  <Chip 
                    label={preset.category} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {preset.description}
                </Typography>

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Requirements:</Typography>
                  {renderCriteriaChips(preset.criteria)}
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip
                    icon={userTypeConfig[preset.userType].icon}
                    label={userTypeConfig[preset.userType].label}
                    size="small"
                    variant="outlined"
                    sx={coloredChipSX(userTypeConfig[preset.userType].color)}
                  />
                  <Chip
                    icon={<CalculateIcon fontSize="small" />}
                    label={`${calculateRecommendedPoints(preset.criteria, preset.userType)} pts`}
                    size="small"
                    color="success"
                    variant="filled"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Privacy and targeting section
  const renderPrivacyAndTargetingSection = () => (
    <Card sx={{ mb: 3, boxShadow: 1 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublicIcon />
          Privacy & Targeting
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Target User Type</InputLabel>
              <Select
                value={userType}
                label="Target User Type"
                onChange={(e) => setUserType(e.target.value)}
              >
                {Object.entries(userTypeConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box display="flex" alignItems="center">
                      {config.icon}
                      <Box ml={1}>
                        <Typography variant="body2">{config.label}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {userTypeConfig[userType]?.description}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {isPrivate ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  <Typography variant="body2">
                    Private Achievement
                  </Typography>
                </Box>
              }
            />
            <FormHelperText sx={{ ml: 4 }}>
              When enabled, achieving this BlitzBolt will not create a public achievement post
            </FormHelperText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Requirements section with scoring
  const renderRequirementsSection = () => (
    <Card sx={{ boxShadow: 1 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon />
            Achievement Requirements
          </Typography>
          
          {currentRecommendedPoints > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Recommended:
              </Typography>
              <Chip
                icon={<CalculateIcon fontSize="small" />}
                label={`${currentRecommendedPoints} points`}
                size="small"
                color="success"
                variant="outlined"
                onClick={() => setNewBoltPoints(currentRecommendedPoints)}
                clickable
              />
              <Tooltip title="Apply recommended points">
                <IconButton 
                  size="small" 
                  onClick={() => setNewBoltPoints(currentRecommendedPoints)}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Scoring Formula</AlertTitle>
          Points are calculated using platform weights: Payouts (User: 1.2, Creator: 0.6), Partnerships (3.0), 
          Campaigns (User: 2.0, Creator: 3.0), Referral pairs (5.0)
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Required Partnerships"
              variant="outlined"
              fullWidth
              type="number"
              value={partnershipRequirement}
              onChange={(e) => setPartnershipRequirement(e.target.value)}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: criteriaConfig.partnership.color }}>
                    {criteriaConfig.partnership.icon}
                  </InputAdornment>
                )
              }}
              helperText={partnershipRequirement > 0 ? `${(partnershipRequirement * 3).toFixed(1)} points` : ''}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Required Campaigns"
              variant="outlined"
              fullWidth
              type="number"
              value={campaignRequirement}
              onChange={(e) => setCampaignRequirement(e.target.value)}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: criteriaConfig.campaign.color }}>
                    {criteriaConfig.campaign.icon}
                  </InputAdornment>
                )
              }}
              helperText={campaignRequirement > 0 ? `${(campaignRequirement * scoringWeights.campaign[userType]).toFixed(1)} points` : ''}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Required Payouts"
              variant="outlined"
              fullWidth
              type="number"
              value={payoutRequirement}
              onChange={(e) => setPayoutRequirement(e.target.value)}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: criteriaConfig.payout.color }}>
                    {criteriaConfig.payout.icon}
                  </InputAdornment>
                )
              }}
              helperText={payoutRequirement > 0 ? 
                `${(payoutRequirement * scoringWeights.payout[userType]).toFixed(1)} points | ${userType === 'creator' ? 'Received' : 'Sent'}` : 
                userType === 'creator' ? 'Received' : 'Sent'
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Required Referrals"
              variant="outlined"
              fullWidth
              type="number"
              value={referralRequirement}
              onChange={(e) => setReferralRequirement(e.target.value)}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: criteriaConfig.referral.color }}>
                    {criteriaConfig.referral.icon}
                  </InputAdornment>
                )
              }}
              helperText="Total referrals made"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Required Successful Referrals"
              variant="outlined"
              fullWidth
              type="number"
              value={successfulReferralRequirement}
              onChange={(e) => setSuccessfulReferralRequirement(e.target.value)}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: criteriaConfig.successful_referral.color }}>
                    {criteriaConfig.successful_referral.icon}
                  </InputAdornment>
                )
              }}
              helperText={
                referralRequirement > 0 && successfulReferralRequirement > 0 ? 
                `${Math.min(referralRequirement, successfulReferralRequirement) * 5} points from pairs` :
                'Referrals that have been completed/converted'
              }
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ padding: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Loading */}
      <Backdrop
        open={!!isBlitzBoltsLoading}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      {renderEnhancedHeader()}

      {/* Filters */}
      {renderFilterSection()}

      {/* Grid of bolts */}
      <Grid container spacing={3}>
        {filteredBolts && filteredBolts.map((bolt) => (
          <Grid item xs={12} sm={6} lg={4} key={bolt.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                border: bolt.attainment?.achieved ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => theme.shadows[8]
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Card header */}
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    sx={{
                      bgcolor:
                        bolt.icon_name === 'Star' ? '#fff3e0' :
                        bolt.icon_name === 'EmojiEvents' ? '#fff3e0' : '#f3e5f5',
                      color:
                        bolt.icon_name === 'Star' ? '#ff9800' :
                        bolt.icon_name === 'EmojiEvents' ? '#ff5722' : '#9c27b0',
                      mr: 2,
                      width: 48,
                      height: 48
                    }}
                  >
                    {iconMap[bolt.icon_name] || <StarIcon />}
                  </Avatar>

                  <Box flexGrow={1} minWidth={0}>
                    <Typography variant="h6" component="h3" noWrap sx={{ fontWeight: 600 }}>
                      {bolt.title}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={`${bolt.points} points`}
                        size="small"
                        variant="outlined"
                      />
                      {bolt.attainment?.achieved && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Achieved"
                          size="small"
                          variant="filled"
                          sx={coloredChipSX('#4CAF50')}
                        />
                      )}
                    </Stack>
                  </Box>
                </Box>

                {/* Metadata chips */}
                <Box sx={{ mb: 2 }}>
                  {renderMetadataChips(bolt)}
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '40px' }}>
                  {bolt.description || 'No description provided'}
                </Typography>

                {/* Progress */}
                {bolt.attainment?.progress && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Progress:
                    </Typography>
                    {renderProgressChips(bolt.attainment.progress, bolt.criteria)}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Requirements */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Requirements:
                  </Typography>
                  {renderCriteriaChips(bolt.criteria)}
                </Box>
              </CardContent>

              {/* Footer */}
              <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  ID: {bolt.id}
                  {bolt.attainment?.achieved_at && (
                    <span> | Achieved: {new Date(bolt.attainment.achieved_at).toLocaleDateString()}</span>
                  )}
                </Typography>
                <Box>
                  <Tooltip title="View Details">
                    <IconButton size="small">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
            Create New BlitzBolt
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define achievement criteria and rewards for your users
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ mb: 3 }}>
            <Tab label="Create Achievement" />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <LightbulbIcon fontSize="small" />
                  Recommendations
                </Box>
              } 
            />
          </Tabs>

          {dialogTab === 0 && (
            <Box>
              {/* Basic information */}
              <Card sx={{ mb: 3, boxShadow: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon />
                    Basic Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Achievement Title"
                        fullWidth
                        variant="outlined"
                        value={newBoltTitle}
                        onChange={(e) => setNewBoltTitle(e.target.value)}
                        placeholder="e.g., First Partnership Master"
                        required
                        sx={{ mb: 2 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        value={newBoltDescription}
                        onChange={(e) => setNewBoltDescription(e.target.value)}
                        placeholder="Describe what users need to accomplish to earn this achievement..."
                        sx={{ mb: 2 }}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel>Icon</InputLabel>
                        <Select
                          value={newBoltIconName}
                          label="Icon"
                          onChange={(e) => setNewBoltIconName(e.target.value)}
                        >
                          {iconOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box display="flex" alignItems="center">
                                {option.icon}
                                <Box ml={1}>{option.label}</Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={4}>
                      <TextField
                        label="Points Reward"
                        fullWidth
                        variant="outlined"
                        type="number"
                        value={newBoltPoints}
                        onChange={(e) => setNewBoltPoints(e.target.value)}
                        inputProps={{ min: 0 }}
                        placeholder={currentRecommendedPoints > 0 ? `Recommended: ${currentRecommendedPoints}` : '0'}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      {currentRecommendedPoints > 0 && (
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ height: '56px' }}
                          startIcon={<TrendingUpIcon />}
                          onClick={() => setNewBoltPoints(currentRecommendedPoints)}
                        >
                          Use {currentRecommendedPoints} pts
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Privacy and targeting */}
              {renderPrivacyAndTargetingSection()}

              {/* Requirements */}
              {renderRequirementsSection()}
            </Box>
          )}

          {dialogTab === 1 && renderRecommendationsTab()}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateBlitzBolt}
            size="large"
            disabled={createBlitzBoltMutation.isLoading || dialogTab === 1}
            startIcon={createBlitzBoltMutation.isLoading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {createBlitzBoltMutation.isLoading ? 'Creating...' : 'Create BlitzBolt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlitzBoltDetailsView;